import { Pipe, PipeTransform } from '@angular/core';

/**
 * The letter e in Javascript represents a number with an exponent power
 * Ex: 3.14e7 equals 3.14*10^7 and 2.6e-7 equals (2.6/10^7) or (2.6*10^-7)
 * Javascript automatically converts any floating point number with at least six zeroes after the decimal point into `e-` notation
 * Ex: In Javascript, `0.0000005` will be displayed as `5e-7`, which is not what we want
 */
@Pipe({
    name: 'removeExponent',
    pure: true
})
export class RemoveExponentPipe implements PipeTransform {
    /**
     * Pipe to remove `e-` in exponential numbers
     * @param value all types of numbers like 3966.79, 0.00101304, 2.2e-7, etc
     * @returns number without any exponent powers
     */
    transform(value: number): string {
        let valueInString = value.toString();
        /* All numbers with e-n notation have absolute values under 1.0 because they are divided by 10^n */
        if (Math.abs(value) < 1.0) {
            /**
             * Convert the value to a string, split it with `e-` and get the exponent power in 2nd index
             * and convert this exponentPower back to a number
             * In 2.3e-7, exponentPower is 7 and in other numbers less than 1.0, exponentPower will be `undefined`
             */
            const exponentPower = parseInt(value.toString().split('e-')[1]);

            /* Number has an `e-` notation */
            if (exponentPower) {
                /**
                 * Remove the e- notation. Ex: 0.0000022 equals 2.2e-6
                 * Multiply the original value with 10^(exponentPower-1)
                 * Ex: 2.2e-6 * 10^5 = 0.22
                 */
                value *= Math.pow(10, exponentPower - 1);

                /**
                 * Re-construct the zeroes after the decimal
                 * Ex: 0.22 will now be 0.0000022
                 */
                valueInString = '0.' + new Array(exponentPower).join('0') + value.toString().substring(2);
            } else {
                valueInString = value.toString();
            }
        } else {
            let e = parseInt(value.toString().split('+')[1]);
            if (e > 20) {
                e -= 20;
                value /= Math.pow(10, e);
                valueInString = value.toString() + new Array(e + 1).join('0');
            }
        }
        return valueInString;
    }
}
